import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock } from "lucide-react";
import { ShapeSquare, ShapeFlower } from "./brand/Shapes";

/**
 * Compact home-page teaser for "Visit the Campus".
 * Heading + intro + CTA — full booking form lives on /contact.
 */
export default function VisitTeaser() {
  return (
    <section
      id="visit-teaser"
      className="relative py-14 md:py-28 bg-[#DCEFE1] overflow-hidden"
      data-testid="visit-teaser-section"
    >
      <ShapeSquare
        size={140}
        color="#648DE5"
        className="absolute -top-10 -left-10 opacity-90 rotate-12 hidden md:block"
      />
      <ShapeFlower
        size={120}
        color="#C6D947"
        className="absolute bottom-10 right-10 opacity-80 hidden md:block"
      />

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-5 block">
              Visit the Campus
            </span>
            <h2 className="font-display text-[#0F1B47] text-[32px] md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold">
              See it for yourself.
              <br />
              <span className="italic font-normal">Take the tour</span>.
            </h2>
            <p className="mt-6 max-w-2xl text-[#4A5568] text-base md:text-lg leading-relaxed font-light">
              Private, guided walks through our studios, gardens and classrooms — weekdays
              & Saturdays. Your child is welcome to join.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="bg-[#FAFAF5] rounded-[32px] p-8 md:p-9 border border-[#0F1B47]/8 shadow-[0_30px_60px_-30px_rgba(15,27,71,0.18)]">
              <div className="flex items-center gap-3 text-[#0F1B47] mb-2">
                <Clock size={16} />
                <span className="text-xs font-bold tracking-[0.18em] uppercase">
                  45 mins · guided
                </span>
              </div>
              <div className="flex items-center gap-3 text-[#4A5568] mb-7">
                <MapPin size={14} />
                <span className="text-sm">Whitefield, Bengaluru — on campus</span>
              </div>
              <Link
                to="/admissions#visit"
                className="group w-full inline-flex items-center justify-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] hover:shadow-xl"
                data-testid="visit-teaser-cta"
              >
                Schedule a Visit
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
              <p className="mt-4 text-xs text-[#718096] text-center font-light">
                Pick a date & time slot on the next page.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
